<script setup lang="ts">
/**
 * CompanyShell - the portal's sidebar layout around every signed-in page.
 *
 * Replaces the old top navigation bar (AppHeader). The shell itself is shared
 * with the admin portal (`@lark-automadoc/shared-ui/shell`); this component
 * owns what is the company portal's: which pages exist, who may see them, the
 * company and plan in the sidebar, and the person's menu.
 *
 * ## Gating mirrors the server, it does not replace it
 *
 * Templates, Approvals, Signatures, Users and API keys are app_admin routes on
 * the API (appAdminMiddleware), so for a member the links would only lead to a
 * refusal. They are hidden — exactly the set the old header hid — and the
 * routes themselves stay reachable by URL, where the page shows the server's
 * answer. Until the tenant has loaded, the gated entries are placeholders
 * rather than absent, so an admin's sidebar does not grow a moment after it
 * first paints.
 *
 * ## Groups (80-global-product-pass §3.2)
 *
 * Entries are named by what people do, never by platform; a platform appears
 * only where the thing is that platform (Lark Approval, Power Automate):
 *
 * - Home
 * - Documents: Documents, Sign, Templates
 * - Automate: API & keys, Power Automate (Microsoft accounts), Lark Approval (Lark)
 * - Company: People, Integrations, Billing (with Compare plans as a tab)
 * - Help: Template gallery, Contact support, Documentation
 *
 * Which screens exist at all depends on the account's one sign-in platform:
 * shared-types `platformFeatures()` (design §8.1), read through the dashboard
 * store. What a session cannot use is not shown, and never explained. A route
 * the session does not get is left for home, here and in the router's guard.
 *
 * Public pages (the signing link, a payment link before sign-in, the sign-in
 * chooser) never reach this component: App.vue renders them before the router,
 * and the full-screen designer opts out with `meta.shell = false`.
 */
import { computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import {
  Home,
  FileText,
  Stamp,
  FolderOpen,
  PenLine,
  Users,
  CreditCard,
  KeyRound,
  LayoutGrid,
  BookOpen,
  LifeBuoy,
  Plug,
  Workflow,
} from 'lucide-vue-next'
import { AppShell, type ShellCrumb, type ShellNavGroup, type ShellNavItem } from '@lark-automadoc/shared-ui/shell'
import { PlanChangeBanner } from '@lark-automadoc/shared-ui'
import { docsUrl } from '@lark-automadoc/shared-types'
import { useDashboardStore } from '@/stores/dashboard'
import { useAuthStore } from '@/stores/auth'
import { DOCS_ORIGIN } from '@/utils/links'
import { contactPreferencesApi } from '@/services/api'
import SidebarBrand from '@/components/shell/SidebarBrand.vue'
import SidebarUsage from '@/components/shell/SidebarUsage.vue'
import UserMenu from '@/components/shell/UserMenu.vue'
import { routeAllowed, signRoute } from '@/router'

const { t, locale } = useI18n()
const route = useRoute()
const router = useRouter()
const dashboard = useDashboardStore()
const auth = useAuthStore()
const { isAppAdmin, loaded, usageStatus, features } = storeToRefs(dashboard)

function link(id: string, path: string, label: string, icon: ShellNavItem['icon']): ShellNavItem {
  const active = path === '/' ? route.path === '/' : route.path === path || route.path.startsWith(`${path}/`)
  return { id, label, icon, href: router.resolve(path).href, active }
}

/**
 * Admin-only entries, or placeholders for them while we do not yet know. A
 * failed tenant load leaves `loaded` true and isAppAdmin false, so a member's
 * view and a failure look alike — the same as the old header. Ungated entries
 * come first in each group below, so the placeholders sit exactly where the
 * gated entries will appear.
 */
function gated(items: ShellNavItem[]): { items: ShellNavItem[]; pending?: number } {
  if (!loaded.value) return { items: [], pending: items.length }
  return { items: isAppAdmin.value ? items : [] }
}

const groups = computed<ShellNavGroup[]>(() => {
  const f = features.value
  // AutomaDoc Sign where the environment has it on (SIGN_AVAILABLE); elsewhere the legacy
  // Signatures page, for the admins of the sessions that have it, as before Sign.
  // Neither until the environment has answered, so one does not flash in for the other.
  const known = auth.providers !== null
  const sign = auth.providers?.sign === true
  const documents = gated([
    link('templates', '/templates', t('navTemplates'), FileText),
    ...(known && !sign && f.signatures ? [link('signatures', '/signatures', t('navSignatures'), PenLine)] : []),
  ])
  const automate = gated([
    link('api-keys', '/api-keys', t('navApiAndKeys'), KeyRound),
    ...(auth.platform === 'microsoft' ? [link('power-automate', '/power-automate', t('navPowerAutomate'), Workflow)] : []),
    ...(f.approvals ? [link('approvals', '/approvals', t('navLarkApproval'), Stamp)] : []),
  ])
  const people = gated([link('people', '/people', t('navPeople'), Users)])
  // Everyone's: the apps are listed for every account, and a member connects their own data here.
  const integrations = [link('integrations', '/integrations', t('navIntegrations'), Plug)]

  return [
    { id: 'overview', items: [link('home', '/', t('navHome'), Home)] },
    {
      id: 'documents',
      label: t('navGroupDocuments'),
      // Sign is everyone's on every platform (members see their own requests).
      items: [link('generations', '/generations', t('navDocuments'), FolderOpen), ...(sign ? [link('esign', '/esign', t('navSign'), PenLine)] : []), ...documents.items],
      pending: documents.pending,
    },
    ...(automate.items.length || automate.pending
      ? [{ id: 'automate', label: t('navGroupAutomate'), items: automate.items, pending: automate.pending }]
      : []),
    {
      id: 'company',
      label: t('navGroupCompany'),
      items: [...people.items, ...integrations, link('billing', '/billing', t('navBilling'), CreditCard)],
      pending: people.pending,
    },
    {
      id: 'help',
      label: t('navGroupHelp'),
      items: [
        link('gallery', '/gallery', t('navTemplateGallery'), LayoutGrid),
        link('contact', '/contact', t('navContactSupport'), LifeBuoy),
        // In the language the portal is showing, not the bare origin, which
        // would re-sniff Accept-Language and ignore the person's choice.
        { id: 'docs', label: t('navDocumentation'), icon: BookOpen, href: docsUrl(undefined, locale.value, DOCS_ORIGIN), external: true },
      ],
    },
  ]
})

/**
 * The page's place in the portal. A switch with literal keys, not a lookup
 * table, so the i18n audit can see every key it uses.
 */
function pageTitle(name: unknown): string {
  switch (name) {
    case 'templates': return t('navTemplates')
    case 'approvals': return t('navLarkApproval')
    case 'approval-detail': return t('template')
    case 'template-detail': return t('template')
    case 'generations': return t('navDocuments')
    case 'signatures': return t('navSignatures')
    case 'esign': return t('navSign')
    case 'esign-envelope': return t('esignEnvelopeCrumb')
    case 'people': return t('navPeople')
    case 'billing': return t('navBilling')
    case 'api-keys': return t('navApiAndKeys')
    case 'power-automate': return t('navPowerAutomate')
    case 'integrations': return t('navIntegrations')
    case 'gallery': return t('navTemplateGallery')
    case 'contact': return t('navContactSupport')
    case 'notifications': return t('notificationsTitle')
    default: return t('navHome')
  }
}

const crumbs = computed<ShellCrumb[]>(() => {
  const current: ShellCrumb = { label: pageTitle(route.name) }
  if (route.name === 'esign-envelope') {
    return [{ label: t('navSign'), href: router.resolve('/esign').href }, current]
  }
  if (route.name === 'approval-detail') {
    return [{ label: t('navLarkApproval'), href: router.resolve('/approvals').href }, current]
  }
  if (route.name === 'template-detail') {
    return [{ label: t('navTemplates'), href: router.resolve('/templates').href }, current]
  }
  return [current]
})

const labels = computed(() => ({
  navigation: t('shellNavigation'),
  openMenu: t('shellOpenMenu'),
  closeMenu: t('shellCloseMenu'),
  collapse: t('shellCollapse'),
  expand: t('shellExpand'),
  skipToContent: t('shellSkipToContent'),
  breadcrumb: t('shellBreadcrumb'),
}))

function navigate(href: string): void {
  // Hash history: the resolved href is "#/path".
  void router.push(href.replace(/^#/, ''))
}

onMounted(() => {
  // Cached — a no-op on pages that already loaded, and what fills the sidebar
  // on the pages that never fetch tenant data themselves.
  void dashboard.load()
  // Which platforms this environment has on decides whether Connections exists
  // at all; a session from inside Lark never asked.
  void useAuthStore().loadProviders()
})

// The person's portal language, so the emails AutomaDoc sends them use it and a broadcast
// can be aimed at it (contact center). Once per language per browser session.
watch(locale, (value) => void contactPreferencesApi.reportLocale(String(value)), { immediate: true })

// The router's guard lets a deep link through while the tenant is unknown;
// once it is, a page this session does not get is left for home.
watch(
  [() => route.fullPath, loaded, features],
  () => {
    if (!routeAllowed(route)) void router.replace('/')
  },
  { immediate: true },
)
// A Sign route let through while the environment had not answered (a blip): once it has,
// go where it says (Sign or the legacy Signatures page).
watch(
  // null until answered, then on or off (an API too old to say counts as off, as in signRoute).
  () => (auth.providers === null ? null : auth.providers.sign === true),
  async () => {
    const target = await signRoute(route)
    if (target !== true) void router.replace(target)
  },
)
</script>

<template>
  <AppShell
    :groups="groups"
    :crumbs="crumbs"
    :labels="labels"
    storage-key="automadoc-company-sidebar"
    :route-key="route.fullPath"
    @navigate="navigate"
  >
    <template #brand="{ collapsed }">
      <SidebarBrand :collapsed="collapsed" />
    </template>

    <template #sidebar-footer="{ collapsed, showTip, hideTip }">
      <SidebarUsage v-if="!collapsed" />
      <UserMenu :collapsed="collapsed" :show-tip="showTip" :hide-tip="hideTip" />
    </template>

    <PlanChangeBanner :usage-status="usageStatus" :docs-origin="DOCS_ORIGIN" :can-purchase="isAppAdmin" @billing="router.push('/billing')" />
    <slot />
  </AppShell>
</template>
